import React, { useEffect, useMemo, useRef } from "react";
import { Spinner, Table, Row, Col } from "react-bootstrap";
import { useParams, useSearchParams } from "react-router";
import { format } from "date-fns";
import { formatPrice } from "../../../../shared/utils/formatPrice";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// ... (semua impor lainnya tetap sama)
import { useShowInvoiceV4 } from "../../../../services/create-booking-v4/hooks/useShowInvoiceV4";
import './InvoiceView1.css';
import { useSkinData } from "../../../../services/common/hooks/useSkinData";
import FullPageSpinner from "../../../../shared/components/spinner/FullPageSpinner";
import FullPageError from "../../../../shared/components/errors/FullPageError";
import useFavicon from "../../../../shared/hooks/useFavicon";
import ExtraDescription from '../../../../shared/components/ExtraDescription';


const baseUrl = window.location.origin;
const base64Url = btoa(baseUrl);

const InvoiceViewComponentNew1V4: React.FC = () => {
    const { id } = useParams<{ id: string }>();

    const { data: invoiceResponse, isFetching, isSuccess, isError } = useShowInvoiceV4({
        invoiceNumber: id || '',
    });

    const invoiceData = invoiceResponse?.data;
    const property = invoiceData?.property;
    const bookingRooms = useMemo(() => {
        if (invoiceData?.bookingRoom && invoiceData.bookingRoom.length > 0) {
            return invoiceData.bookingRoom;
        }
        return invoiceData?.booking ? [invoiceData.booking] : [];
    }, [invoiceData]);

    const primaryBooking = bookingRooms[0];
    const totalPayments = invoiceData?.payments?.reduce((sum, p) => sum + p.amount + p.surcharge, 0) ?? 0;

    const [searchParams] = useSearchParams();
    const pdfRef = useRef<HTMLDivElement>(null);
    const isDownloadAction = searchParams.has("download");

    const { data, isLoading, isError: isErrorSkin } = useSkinData(base64Url);
    useFavicon(data?.data?.favicon_url || null);
    const themeColor = data?.data?.color || '#076a4e';

    const generatePDF = React.useCallback(async () => {
        const input = pdfRef.current;
        if (!input) return;

        try {
            const canvas = await html2canvas(input, {
                scale: 2,
                useCORS: true,
                logging: false,
            });

            const imgData = canvas.toDataURL("image/png");

            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4"
            });

            const imgWidth = 210;
            const pageHeight = 297;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft >= 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            pdf.save(`Invoice-${primaryBooking?.invoice_number || id}.pdf`);
        } catch (error) {
            console.error("Gagal membuat PDF:", error);
            alert("An error occurred while generating the PDF.");
        }
    }, [id, primaryBooking?.invoice_number]);

    useEffect(() => {
        if (isSuccess && !isFetching && invoiceData && isDownloadAction) {
            generatePDF();
        }
    }, [isSuccess, isFetching, invoiceData, isDownloadAction, generatePDF]);

    useEffect(() => {
        const root = document.documentElement;
        root.style.setProperty('--tblr-primary', themeColor);
        document.title = data?.data?.name || 'Admin Panel';
        return () => {
            root.style.removeProperty('--tblr-primary');
            document.title = 'Admin Panel';
        };
    }, [themeColor, data?.data?.name]);

    if (!id) {
        return <div className="invoice-modern-page"><h4>Invoice number is required.</h4></div>;
    }

    if (isError) {
        return <div className="invoice-modern-page"><h4>Invoice not found. Please check the number and try again.</h4></div>;
    }

    if (isLoading) {
        return <div className="invoice-modern-page"><FullPageSpinner /></div>;
    }

    if (isErrorSkin) {
        return <div className="invoice-modern-page"><FullPageError message="Failed to load skin data. Please try again later." /></div>;
    }

    return (
        <div className="invoice-modern-page" ref={pdfRef}>
            {isFetching ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                    <Spinner animation="border" />
                    <span className="ms-3">Loading Invoice...</span>
                </div>
            ) : isSuccess && primaryBooking && property ? (
                <div className="invoice-container" style={{ width: "210mm", margin: "0 auto", overflow: 'hidden' }}>
                    {/* ... (seluruh JSX Anda di sini tetap sama) ... */}
                    <header className="invoice-header d-flex justify-content-between align-items-center">
                        <div>
                            <h2>INVOICE</h2>
                            <p>#{primaryBooking.invoice_number}</p>
                        </div>
                        {property.logo_url && (
                            <img src={property.logo_url} alt="Property Logo" className="invoice-logo" />
                        )}
                    </header>
                    <main className="invoice-body">
                        <Row className="billing-details mb-5">
                             <Col md={6} style={{ minWidth: 0, overflow: 'hidden' }}>
                                 <h6>BILL TO</h6>
                                 <p style={{ wordBreak: 'break-word' }}>{`${primaryBooking.customer_name} ${primaryBooking.customer_surname || ""}`.trim()}</p>
                                 <p className="text-muted" style={{ wordBreak: 'break-word' }}>{[primaryBooking.customer_address, primaryBooking.customer_state, primaryBooking.customer_city, primaryBooking.customer_country].filter(Boolean).join(", ")}</p>
                                 {primaryBooking.customer_email && (
                                     <p className="mb-1" style={{ wordBreak: 'break-word' }}>
                                         <a href={`mailto:${primaryBooking.customer_email}`} className="invoice-link">{primaryBooking.customer_email}</a>
                                     </p>
                                 )}
                                 {primaryBooking.customer_phone && (
                                     <p className="text-muted mb-0" style={{ wordBreak: 'break-word' }}>Tel: {primaryBooking.customer_phone}</p>
                                 )}
                             </Col>
                             <Col md={6} className="text-md-end" style={{ minWidth: 0, overflow: 'hidden' }}>
                                 <h6>FROM</h6>
                                 <p style={{ wordBreak: 'break-word' }}>{property.name}</p>
                                 <p className="text-muted" style={{ wordBreak: 'break-word' }}>{[property.street_address, property.city, property.state].filter(Boolean).join(", ")}</p>
                                 {property.primary_email && (
                                     <p className="mb-1" style={{ wordBreak: 'break-word' }}>
                                         <a href={`mailto:${property.primary_email}`} className="invoice-link">{property.primary_email}</a>
                                     </p>
                                 )}
                                 {(property.primary_phone_country_code || property.primary_phone) && (
                                     <p className="text-muted mb-0" style={{ wordBreak: 'break-word' }}>Tel: {`${property.primary_phone_country_code ?? ""} ${property.primary_phone ?? ""}`.trim()}</p>
                                 )}
                             </Col>
                        </Row>
                        <Row className="billing-details mb-4">
                            <Col>
                                <h6>Invoice Date</h6>
                                <p>{format(new Date(primaryBooking.invoice_date), 'dd MMMM yyyy')}</p>
                            </Col>
                        </Row>

                        <div className="bg-light rounded p-4 mb-4 text-center">
                            <Row>
                                <Col md={6}>
                                    <div className="text-muted fw-bold mb-1" style={{ fontSize: '0.85rem' }}>CHECK-IN:</div>
                                    {property.checkin_time && (
                                        <div className="mb-1 text-dark">({property.checkin_time})</div>
                                    )}
                                    <h6 className="fw-bold text-dark fs-5">{format(new Date(primaryBooking.checkin_date), 'EEEE, dd MMMM yyyy')}</h6>
                                </Col>
                                <Col md={6}>
                                    <div className="text-muted fw-bold mb-1" style={{ fontSize: '0.85rem' }}>CHECK-OUT:</div>
                                    {property.checkout_time && (
                                        <div className="mb-1 text-dark">({property.checkout_time})</div>
                                    )}
                                    <h6 className="fw-bold text-dark fs-5">{format(new Date(primaryBooking.checkout_date), 'EEEE, dd MMMM yyyy')}</h6>
                                </Col>
                            </Row>
                        </div>
                        <Table responsive borderless className="items-table">
                            <thead>
                                <tr>
                                    <th>DESCRIPTION</th>
                                    <th className="text-end">PRICE</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bookingRooms.map((room, index) => (
                                    <React.Fragment key={room.id}>
                                        <tr>
                                            <td>
                                                <strong>Accommodation Charge (Room {index + 1})</strong>
                                                <small className="text-muted d-block">{room.room_type?.name} - {room.rate_plan?.name}</small>
                                                <small className="text-muted d-block">Occupancy: {room.adults} Adults, {room.children} Children</small>
                                            </td>
                                            <td className="text-end">{formatPrice(room.total_rate)}</td>
                                        </tr>
                                        {room.booking_rates?.map((rate, rateIndex) => (
                                            <tr key={`${room.id}-rate-${rateIndex}`}>
                                                <td className="ps-4 text-muted">
                                                    Rate for {format(new Date(rate.date), 'dd MMMM yyyy')}
                                                </td>
                                                <td className="text-end text-muted">{formatPrice(rate.rate)}</td>
                                            </tr>
                                        ))}
                                        {room.booking_extras && room.booking_extras.length > 0 && (
                                            <>
                                                <tr>
                                                    <td className="ps-4">
                                                        <strong className="text-muted">Extras</strong>
                                                    </td>
                                                    <td className="text-end"><strong>{formatPrice(room.total_extras ?? 0)}</strong></td>
                                                </tr>
                                                {room.booking_extras.map((extra, extraIndex) => (
                                                    <React.Fragment key={`${room.id}-extra-${extraIndex}`}>
                                                        <tr>
                                                            <td className="ps-5 text-muted">
                                                                {extra.item_name} (x{extra.qty})
                                                            </td>
                                                            <td className="text-end text-muted">{formatPrice(extra.subtotal)}</td>
                                                        </tr>
                                                        {extra.description && (
                                                            <tr>
                                                                {/* Rincian item & pajak dari POS — tamu perlu bisa
                                                                    mencocokkannya dengan struk. */}
                                                                <td className="ps-5 pt-0 border-0" colSpan={2}>
                                                                    <ExtraDescription description={extra.description} small />
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </Table>
                        {invoiceData?.payments && invoiceData.payments.length > 0 && (
                            <div className="mt-5">
                                <h6 className="mb-3 fw-bold">Payments Received</h6>
                                <Table responsive borderless className="items-table">
                                    <thead><tr><th>PAYMENT DATE</th><th>METHOD</th><th className="text-end">AMOUNT</th></tr></thead>
                                    <tbody>
                                        {invoiceData.payments.map(payment => (
                                            <tr key={payment.id}>
                                                <td>{format(new Date(payment.date_payment), 'dd MMMM yyyy')}</td>
                                                <td>{payment.payment_type.replace(/_/g, " ")}</td>
                                                <td className="text-end">{formatPrice(payment.amount + payment.surcharge)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        <div className="d-flex justify-content-end mt-4">
                            <div className="col-md-5">
                                <div className="invoice-summary">
                                    <div className="summary-item d-flex justify-content-between">
                                        <span>Subtotal</span>
                                        <span>{formatPrice(invoiceData.invoice_total)}</span>
                                    </div>
                                    <div className="summary-item d-flex justify-content-between">
                                        <span>Total Payments</span>
                                        <span className="text-success">- {formatPrice(totalPayments)}</span>
                                    </div>
                                    <div className="summary-item grand-total d-flex justify-content-between">
                                        <strong>Amount Due</strong>
                                        <span className="text-danger">{formatPrice(invoiceData.due_payment)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {property.banks && property.banks.length > 0 && (
                            <div className="invoice-notes mt-5">
                                <strong>Payment Instructions:</strong>
                                <p className="text-muted mb-2">Please make the payment to the following account:</p>
                                {property.banks.map(bank => (
                                    <div key={bank.id} className="mb-2">
                                        <strong>{bank.bank_name.toUpperCase()}</strong>
                                        <div className="text-muted small">Account Name: {bank.account_name}</div>
                                        <div className="text-muted small">Account Number: {bank.account_number}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </main>
                </div>
            ) : null}
        </div>
    );
};

export default InvoiceViewComponentNew1V4;